import React, { useEffect, useLayoutEffect, useState } from "react";
import { Provider } from "react-redux";
import { HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "react-toastify";
import { store, useAppDispatch } from "./store";
import { useRoutes, useLocation } from "react-router-dom";
import { isLoggedIn } from "./store/actions/auth";

import router from "./router";
import LoadingScreen from "components/layout/LoadingScreen";
import "./firebase";

import "react-toastify/dist/ReactToastify.css";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "react-loading-skeleton/dist/skeleton.css";
import "./styles/globals.css";

import {
  getAdvancedOptions,
  getCategories,
  getLensColors,
  getLenses,
  getLensMaterial,
  getOptions,
} from "store/actions/global";

interface Props {
  children: React.ReactNode;
}

const FetchState: React.FC<Props> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const dispatch = useAppDispatch();

  const checkAuthState = async () => {
    try {
      await dispatch(isLoggedIn());
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const initStoreData = () => {
    checkAuthState();
    dispatch(getCategories());
    dispatch(getLenses());
    dispatch(getLensMaterial());
    dispatch(getLensColors());
    dispatch(getOptions());
    dispatch(getAdvancedOptions());
  };

  useEffect(() => {
    initStoreData();
  }, []);

  if (loading) return <LoadingScreen />;

  return <>{children}</>;
};

const RenderRoutes = () => {
  const location = useLocation();
  const routes = useRoutes(router());
  return routes;
};

const Wrapper: React.FC = () => {
  const location = useLocation();
  useLayoutEffect(() => {
    document.documentElement?.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Provider store={store}>
      <HelmetProvider>
        <FetchState>
          <React.Suspense fallback={<LoadingScreen />}>
            <RenderRoutes />
          </React.Suspense>
        </FetchState>
      </HelmetProvider>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        rtl={true}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Provider>
  );
};

export default Wrapper;
